import { useMemo } from 'react';
import { getId, useAppClockService } from '@mews/framework';
import { Product, ReservationStatus, Service, ServiceType } from '@mews-commander/data-access';
import {
    ClaimedProductsBy,
    ProductCheckListFilterValues,
} from '../components/panels/ProductCheckListFilterFields/types';
import { ProductCheckListTableColumn } from '../containers/ProductCheckListTable/types';
import { useProductChecklistFiltersStorage } from './useProductChecklistFiltersStorage';

const mapProducts = (item: { service: Service; products: Product[] }) => item.products.map(getId);

export const useProductCheckListFiltersDefaultValue = (
    productsByServiceType: Record<
        ServiceType.Bookable | ServiceType.Additional,
        { service: Service; products: Product[] }[]
    >
): ProductCheckListFilterValues => {
    const appClockService = useAppClockService();

    const localStorage = useProductChecklistFiltersStorage();

    const productIds = useMemo(
        () => ({
            ...Object.values(productsByServiceType[ServiceType.Bookable]).reduce((acc, item) => {
                const products = mapProducts(item);
                return { ...acc, ...(products.length && { [item.service.id]: products }) };
            }, {}),
            [ServiceType.Additional.toLowerCase()]: Object.values(
                productsByServiceType[ServiceType.Additional]
            ).flatMap(mapProducts),
        }),
        [productsByServiceType]
    );

    const defaultFilters = useMemo(
        (): Omit<ProductCheckListFilterValues, 'date'> => ({
            productIds,

            reservationStatus: Object.values(ReservationStatus),
            displayReservationsWithoutProducts: false,

            displayedColumns: [ProductCheckListTableColumn.Guests, ProductCheckListTableColumn.Owner],
            claimedProducts: ClaimedProductsBy.Total,
        }),
        [productIds]
    );

    const date = appClockService.getAppNowUtc().value;

    const savedFilters = localStorage.getSafe();
    const filters = savedFilters ?? defaultFilters;

    return { ...filters, date };
};
